import { DestroyRef, inject, Injectable, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TosDocumentModel } from '@libs/account/models';
import { ModalModel, ModalPanelComponent } from '@libs/common/components';
import { AuthService } from '@libs/shared/auth';
import { DialogService, EnvironmentService, ErrorHandlerService } from '@libs/shared/common';
import { UserService } from '@libs/shared/services';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class TosService {
  private readonly authService: AuthService = inject(AuthService);
  private readonly environmentService: EnvironmentService = inject(EnvironmentService);
  private readonly userService: UserService = inject(UserService);
  private readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);
  private readonly dialog: DialogService = inject(DialogService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private readonly isUserAuthorized: Signal<boolean> = toSignal(
    this.authService.isUserAuthorized$('user'),
    { initialValue: false },
  );

  private readonly dialogSettings: MatDialogConfig = {
    width: '80vw',
    maxWidth: '1200px',
    maxHeight: '80vh',
  };

  public initialize(): void {
    const appId: string | undefined = this.environmentService.msalConfig().clientId;

    if (!appId) {
      return;
    }

    this.authService.isUserAuthorized$('user').pipe(
      filter(Boolean),
      switchMap(() => this.userService.checkTos(appId).pipe(
        // Keep the auth-event subscription alive when a TOS check fails.
        catchError(error => {
          this.errorHandler.handleError(error);

          return EMPTY;
        }),
      )),
      filter(Boolean),
      takeUntilDestroyed(this.destroyRef),
    )
      .subscribe(
        (document: TosDocumentModel) => void this.handleDocument(document),
      );
  }

  public openCurrentTosDialog(): boolean {
    if (!this.isUserAuthorized()) {
      return false;
    }

    const appId: string | undefined = this.environmentService.msalConfig().clientId;

    if (!appId) {
      return false;
    }

    this.userService.currentTos(appId).pipe(
      take(1),
      switchMap(document => document ? this.openDialog(document, false) : EMPTY),
    ).subscribe();

    return true;
  }

  private handleDocument(document: TosDocumentModel): void {
    this.openDialog(document, true)
      .pipe(
        switchMap(dialogRef => dialogRef ? dialogRef.afterClosed() : of(undefined)),
        switchMap(result => result === 'accept'
          ? this.userService.consentTos(String(document.id))
          : this.authService.logout(),
        ),
      ).subscribe();
  }

  private openDialog(document: TosDocumentModel, requireConfirmation: boolean): Observable<MatDialogRef<ModalPanelComponent> | undefined> {
    const title: string = 'Nutzungsbedingungen';
    const content: string = document.body;
    const acceptLabel: string = requireConfirmation ? 'Zustimmen' : 'Schließen';
    const cancelLabel: string = requireConfirmation ? 'Abbrechen' : '';

    return this.dialog.queueDialog<ModalPanelComponent, ModalModel>(ModalPanelComponent, {
      ...this.dialogSettings,
      disableClose: requireConfirmation,
      data: { title, content, acceptLabel, cancelLabel } satisfies ModalModel,
    });
  }
}
